import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/scan/$id')({
  component: ScanPage,
  validateSearch: (search: Record<string, unknown>) => ({
    planId: (search.planId as string) || undefined,
  }),
})

type SkinContext = {
  id: string
  scanId: string
  skinTone?: string
  skinType?: string
  concerns: string[]
  recommendations: string[]
  createdAt: string
}

type ScanStatus = {
  id: string
  status: string
  skinContext?: SkinContext
  createdAt: string
  updatedAt: string
}

const STATUS_LABELS: Record<string, string> = {
  idle: 'Preparing...',
  consented: 'Consent recorded',
  capturing: 'Capturing image...',
  validating: 'Validating image...',
  uploading: 'Uploading...',
  queued: 'Queued for analysis',
  processing: 'Analyzing your skin...',
  completed: 'Analysis complete',
  failed: 'Analysis failed',
  expired: 'Scan expired',
  deleted: 'Scan deleted',
}

const CONCERN_LABELS: Record<string, string> = {
  dryness: 'Dryness',
  oiliness: 'Oiliness',
  uneven_tone: 'Uneven tone',
  redness: 'Redness',
  texture: 'Visible texture',
  pores: 'Visible pores',
  dark_spots: 'Dark spots',
  fine_lines: 'Fine lines',
}

function ScanPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const search = Route.useSearch() as { planId?: string }
  const planId = search.planId
  const [scan, setScan] = React.useState<ScanStatus | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const fetchScan = React.useCallback(async () => {
    try {
      const res = await fetch(`/api/v1/skin-scans/${id}`)
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Scan not found')
        }
        throw new Error('Failed to fetch scan status')
      }
      const data = await res.json()
      setScan(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => {
    fetchScan()
    // Poll for status updates when processing
    if (scan?.status === 'queued' || scan?.status === 'processing') {
      const interval = setInterval(fetchScan, 3000)
      return () => clearInterval(interval)
    }
  }, [fetchScan, scan?.status])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted">Loading scan...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="rounded-lg bg-danger/10 p-4 text-center">
          <p className="text-danger font-medium">Error</p>
          <p className="text-sm text-danger/80 mt-1">{error}</p>
        </div>
        <button
          type="button"
          onClick={fetchScan}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (!scan) {
    return null
  }

  const isProcessing = scan.status === 'queued' || scan.status === 'processing'
  const isComplete = scan.status === 'completed'
  const isFailed = scan.status === 'failed'
  const scanSteps = [
    {
      label: 'Photo uploaded',
      active: ['uploading', 'queued', 'processing', 'completed'].includes(scan.status),
      done: ['queued', 'processing', 'completed'].includes(scan.status),
    },
    {
      label: 'Analysis queued',
      active: ['queued', 'processing', 'completed'].includes(scan.status),
      done: ['processing', 'completed'].includes(scan.status),
    },
    {
      label: 'Skin context ready',
      active: scan.status === 'completed',
      done: scan.status === 'completed',
    },
  ]

  return (
    <div className="ootopia-pwa-page ootopia-scan-page ootopia-pwa-stack">
      <div className="ootopia-scan-intro">
        <p className="ootopia-section-kicker">Skin scan</p>
        <h1 className="ootopia-scan-heading mt-2">
          Your look starts <em>with you.</em>
        </h1>
      </div>

      {/* Status card */}
      <div className="ootopia-pwa-surface ootopia-pwa-status-card p-5">
        <div className="flex items-center gap-3">
          {isProcessing && (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          )}
          {isComplete && (
            <div className="rounded-full bg-success/10 p-1">
              <svg
                className="h-4 w-4 text-success"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>Success</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}
          {isFailed && (
            <div className="rounded-full bg-danger/10 p-1">
              <svg
                className="h-4 w-4 text-danger"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>Error</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}
          <div>
            <p className="font-medium">
              {STATUS_LABELS[scan.status] ?? 'Status update in progress'}
            </p>
            <p className="text-sm text-muted">{new Date(scan.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </div>

      <section
        className="ootopia-pwa-surface ootopia-pwa-timeline"
        aria-label="Skin analysis progress"
      >
        {scanSteps.map((step, index) => (
          <div
            key={step.label}
            className={`ootopia-pwa-timeline__item ${step.active && !step.done ? 'is-active' : ''} ${step.done ? 'is-done' : ''}`}
          >
            <span className="ootopia-pwa-timeline__dot" aria-hidden="true">
              {step.done ? '✓' : index + 1}
            </span>
            <div className="ootopia-pwa-timeline__copy">
              <strong>{step.label}</strong>
              <span>{step.done ? 'Complete' : step.active ? 'In progress' : 'Up next'}</span>
            </div>
          </div>
        ))}
      </section>

      {/* Processing state */}
      {isProcessing && (
        <div className="ootopia-pwa-surface ootopia-pwa-processing-card p-5">
          <p className="ootopia-section-kicker">Reading your skin</p>
          <h2 className="mt-2">A clear view, taking shape.</h2>
          <p className="text-sm text-muted">
            We&apos;re reading tone, undertone, texture, sensitivity, and comfort. This usually
            takes 10-30 seconds.
          </p>
          <div
            className="ootopia-scan-progress-track mt-5"
            role="progressbar"
            aria-label="Skin analysis in progress"
            aria-valuetext="Analyzing your skin"
          >
            <div className="ootopia-scan-progress-beam" />
          </div>
        </div>
      )}

      {/* Skin context results */}
      {isComplete && scan.skinContext && (
        <div className="flex flex-col gap-4">
          <div className="ootopia-pwa-surface ootopia-scan-results-card p-5">
            <h2 className="ootopia-ref-display ootopia-scan-results-title mb-4">
              Your skin, <em>read with care.</em>
            </h2>

            {/* Skin tone and type */}
            <div className="ootopia-scan-results-grid mb-4 grid grid-cols-2 gap-4">
              {scan.skinContext.skinTone && (
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted uppercase tracking-wide">Skin Tone</p>
                  <p className="font-medium mt-1 capitalize">{scan.skinContext.skinTone}</p>
                </div>
              )}
              {scan.skinContext.skinType && (
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted uppercase tracking-wide">Skin Type</p>
                  <p className="font-medium mt-1 capitalize">{scan.skinContext.skinType}</p>
                </div>
              )}
            </div>

            {/* Concerns */}
            {scan.skinContext.concerns.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Visible Characteristics</p>
                <div className="flex flex-wrap gap-2">
                  {scan.skinContext.concerns.map((concern) => (
                    <span key={concern} className="rounded-full bg-muted px-3 py-1 text-sm">
                      {CONCERN_LABELS[concern] ?? concern}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {scan.skinContext.recommendations.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Suggested Considerations</p>
                <ul className="space-y-2">
                  {scan.skinContext.recommendations.map((rec) => (
                    <li key={rec} className="flex items-start gap-2 text-sm">
                      <span
                        aria-hidden="true"
                        className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                      />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Limitations disclosure */}
          <div className="ootopia-pwa-disclosure">
            <p className="text-xs text-muted leading-relaxed">
              <strong>Important:</strong> This analysis describes visible characteristics of your
              skin in the uploaded photo. It is not a medical diagnosis. Results may vary based on
              lighting, camera quality, and other factors. Always consult a dermatologist for skin
              concerns.
            </p>
          </div>

          {/* Continue button */}
          {planId ? (
            <button
              type="button"
              onClick={() => {
                navigate({
                  to: '/remix/$id/goals',
                  params: { id: planId },
                  search: { scanId: id },
                })
              }}
              className="w-full rounded-xl bg-primary py-3 text-primary-foreground font-medium"
            >
              Continue to Goal Selection
            </button>
          ) : (
            <Link
              to="/looks"
              className="ootopia-scan-continue w-full rounded-xl border border-primary py-3 text-center font-medium text-primary"
            >
              Choose a look to continue
            </Link>
          )}
        </div>
      )}

      {/* Failed state */}
      {isFailed && (
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h2 className="font-semibold mb-2">Analysis Failed</h2>
          <p className="text-sm text-muted">
            We couldn't analyze your skin photo. This could be due to:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted">
            <li className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              />
              <span>No face detected in the photo</span>
            </li>
            <li className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              />
              <span>Multiple faces detected</span>
            </li>
            <li className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              />
              <span>Poor image quality or lighting</span>
            </li>
            <li className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              />
              <span>Technical issue with the analysis service</span>
            </li>
          </ul>
          <button
            type="button"
            onClick={() => {
              // Navigate back to capture
              window.location.href = '/capture'
            }}
            className="mt-4 w-full rounded-xl bg-primary py-3 text-primary-foreground font-medium"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  )
}
